import type { Manifest } from '../types';
import { evaluateSession } from '../session/eligibility';
import { manifest } from '../state/manifest';
import { settings, updateSettings } from '../state/settings';

const SHARES = [0.15, 0.25, 0.4] as const;

export interface SpeechShareSummary {
  approvedRecordings: number;
  applicableRecordings: number;
  actualMin: number | null;
  actualMax: number | null;
}

export function speechShareSummary(
  library: Manifest | null, targetSec: number, cadence: 'opening' | 'occasional' | 'frequent',
  desiredSpeechRatio: number | null,
): SpeechShareSummary {
  if (!library) return { approvedRecordings: 0, applicableRecordings: 0, actualMin: null, actualMax: null };
  const approvedRecordings = library.recordings.filter((recording) =>
    [...(recording.spanVariants ?? []), ...(recording.phaseVariants?.opening ?? []), ...(recording.phaseVariants?.closing ?? [])]
      .some((variant) => variant.review?.status === 'approved')).length;
  if (desiredSpeechRatio === null) {
    return { approvedRecordings, applicableRecordings: 0, actualMin: null, actualMax: null };
  }
  const actual: number[] = [];
  for (const recording of library.recordings) {
    const result = evaluateSession({ recording, targetSec, cadence, desiredSpeechRatio });
    if (result.eligible && (result.plan.composition?.variants.length ?? 0) > 0) {
      actual.push(result.plan.preview.spokenSec / result.plan.actualSec);
    }
  }
  return {
    approvedRecordings,
    applicableRecordings: actual.length,
    actualMin: actual.length ? Math.min(...actual) : null,
    actualMax: actual.length ? Math.max(...actual) : null,
  };
}

export function SpeechSharePreference() {
  const s = settings.value;
  const summary = speechShareSummary(
    manifest.value, s.lastDurationSec, s.cadence, s.desiredSpeechRatio,
  );
  const available = summary.approvedRecordings > 0;
  return (
    <section class="stack-sm" aria-labelledby="speech-share-heading">
      <h3 class="eyebrow" id="speech-share-heading">Speech share</h3>
      <div class="row" style={{ flexWrap: 'wrap', gap: 'var(--gap-1)' }}>
        <button
          class="chip"
          aria-pressed={s.desiredSpeechRatio === null}
          onClick={() => updateSettings({ desiredSpeechRatio: null })}
        >Cadence balance</button>
        {SHARES.map((ratio) => (
          <button
            key={ratio}
            class="chip"
            disabled={!available}
            aria-pressed={s.desiredSpeechRatio === ratio}
            onClick={() => updateSettings({ desiredSpeechRatio: ratio })}
          >About {Math.round(ratio * 100)}% speech</button>
        ))}
      </div>
      <p class="dim" style={{ fontSize: 'var(--step--1)' }}>
        {!available
          ? 'No shorter boundary passages have been reviewed yet, so recordings keep their full opening and closing.'
          : s.desiredSpeechRatio === null
            ? `${summary.approvedRecordings} recordings offer reviewed shorter boundary passages. `
              + 'Cadence sets the speech band; a share inside it is a preference; the home preview shows the actual result.'
            : summary.applicableRecordings === 0
              ? 'No recording at the current length can use that reviewed alternative within the cadence band.'
              : `${summary.applicableRecordings} recordings at the current length can use a reviewed alternative; `
                + `their actual speech share is ${Math.round((summary.actualMin ?? 0) * 100)}`
                + `${summary.actualMax !== summary.actualMin
                  ? `–${Math.round((summary.actualMax ?? 0) * 100)}` : ''}%. `
                + 'The home preview shows the selected recording’s exact minutes.'}
      </p>
    </section>
  );
}
